import { composeHeadline } from "@/shared/intelligence/peer-cohort-format.ts";
import { usePeerCohort } from "../hooks/use-peer-cohort.ts";

interface PeerCohortHeadlineProps {
  projectId: string | null | undefined;
}

/**
 * Single-line peer-cohort headline displayed at the top of the project
 * detail modal. Always uses the Twins lens — the user can change cohort
 * lenses in the full Comparables panel below. Hides cleanly on loading
 * states, errors, and projects with too few peers (cohort: null).
 *
 * The headline string is composed by the canonical `composeHeadline`
 * helper in the service layer, so the operator sees the same phrasing
 * across the headline, panel, and any future surfaces.
 */
export function PeerCohortHeadline({ projectId }: PeerCohortHeadlineProps) {
  const { data, isLoading, error } = usePeerCohort(projectId, "twins");

  if (isLoading) {
    return (
      <output className="peer-headline peer-headline--skeleton" aria-label="Loading peer cohort">
        <span className="peer-headline__pill peer-headline__pill--skeleton" />
      </output>
    );
  }

  if (error || !data) return null;
  const headline = composeHeadline(data);
  if (!headline) return null;

  return (
    <div className="peer-headline" data-testid="peer-headline">
      <span className="peer-headline__rank">{headline.rankSummary}</span>
      {headline.topEdge && (
        <>
          <span className="peer-headline__sep">·</span>
          <span className="peer-headline__edge">{headline.topEdge}</span>
        </>
      )}
      {headline.trajectoryLabel && (
        <>
          <span className="peer-headline__sep">·</span>
          <span
            className={
              headline.trajectoryLabel.startsWith("Growing faster") ||
              headline.trajectoryLabel.startsWith("Fastest")
                ? "peer-headline__traj peer-headline__traj--positive"
                : "peer-headline__traj peer-headline__traj--negative"
            }
          >
            {headline.trajectoryLabel}
          </span>
        </>
      )}
    </div>
  );
}
